import { chainError } from "./utils";

declare global {
    interface Window {
        webkitIndexedDB?: typeof window.indexedDB;
        mozIndexedDB?: typeof window.indexedDB;
        oIndexedDB?: typeof window.indexedDB;
        msIndexedDB?: typeof window.indexedDB;
        shimIndexedDB?: typeof window.indexedDB;
    }
}

const IDB = window.indexedDB || window.webkitIndexedDB || window.mozIndexedDB ||
    window.oIndexedDB || window.msIndexedDB || window.shimIndexedDB;

if (IDB === window.shimIndexedDB) {
    console.warn('WARNING: using IndexedDBShim');
}
export class StorageError extends Error {
    constructor(message: string) {
        super(message);
        Object.defineProperty(this, 'name', { value: 'StorageError' });
        Object.setPrototypeOf(this, new.target.prototype);
        Error?.captureStackTrace(this, StorageError);
    }
}

export function toStorageError(x: unknown): Error {
    return chainError(StorageError, x, 'Storage operation failed');
}

let db: IDBDatabase | undefined;

export async function openDatabase(): Promise<IDBDatabase> {
    if (db) {
        return db;
    }
    return new Promise((resolve, reject): void => {
        try {
            const openRequest = IDB.open('practique', 10);
            openRequest.onblocked = (): void => {
                console.error('open database blocked:', String(openRequest.error));
                reject(toStorageError(openRequest.error));
            };
            openRequest.onerror = (): void => {
                console.error('open database error: ', String(openRequest.error));
                reject(toStorageError(openRequest.error));
            };
            openRequest.onupgradeneeded = (event: IDBVersionChangeEvent): void => {
                console.log('upgrade needed');
                if (event.newVersion != null && event.oldVersion != event.newVersion) {
                    console.log('recreating database');
                    const db = openRequest.result;
                    if (db.objectStoreNames.contains('exams')) {
                        db.deleteObjectStore("exams");
                    }
                    if (db.objectStoreNames.contains('encrypted')) {
                        db.deleteObjectStore("encrypted");
                    }
                    if (db.objectStoreNames.contains('questions')) {
                        db.deleteObjectStore('questions');
                    }
                    if (db.objectStoreNames.contains('images')) {
                        db.deleteObjectStore('images');
                    }
                    if (db.objectStoreNames.contains('answers')) {
                        db.deleteObjectStore('answers');
                    }
                    if (db.objectStoreNames.contains('status')) {
                        db.deleteObjectStore('status');
                    }
                    if (db.objectStoreNames.contains('flags')) {
                        db.deleteObjectStore('flags');
                    }
                    if (db.objectStoreNames.contains('users')) {
                        db.deleteObjectStore('users');
                    }
                    if (db.objectStoreNames.contains('session')) {
                        db.deleteObjectStore('session');
                    }
                    if (db.objectStoreNames.contains('notes')) {
                        db.deleteObjectStore('notes');
                    }
                    db.createObjectStore('exams', {keyPath: ['id', 'component']});
                    db.createObjectStore('encrypted');
                    db.createObjectStore('questions');
                    db.createObjectStore('images');
                    const answerStore = db.createObjectStore('answers', {
                        keyPath: ['examId', 'candidateId', 'qno', 'ano']
                    });
                    answerStore.createIndex('statusIndex', ['statusRemote', 'statusEmpty'], {unique: false});
                    db.createObjectStore('flags');
                    db.createObjectStore('users');
                    db.createObjectStore('session');
                    db.createObjectStore('notes');
                }
            };
            openRequest.onsuccess = (): void => {
                db = openRequest.result;
                db.onversionchange = () => {
                    console.log('DB_VERSION_CHANGE');
                    if (db) {
                        db.close();
                        db = undefined;
                    }
                }
                db.onclose = () => {
                    console.log('DB_CLOSED');
                    db = undefined;
                }
                resolve(db);
            };
        } catch (err) {
            console.error('error: openDatabase', String(err));
            reject(toStorageError(err));
        }
    });
}

export async function dbCount(store: string, key?: IDBValidKey): Promise<number> {
    const db = await openDatabase();
    return new Promise<number>((resolve, reject): void => {
        try {
            const transaction = db.transaction([store], "readonly");
            transaction.onabort = () => reject(toStorageError(transaction.error));
            transaction.onerror = () => reject(toStorageError(transaction.error));
            const request = transaction.objectStore(store).count(key);
            request.onsuccess = () => {
                try {
                    transaction.commit?.();
                    const result = request.result;
                    if (typeof result !== 'number') {
                        throw new StorageError('Invalid count result');
                    }
                    resolve(result);
                } catch (err) {
                    reject(toStorageError(err));
                }
            }
        } catch (err) {
            reject(toStorageError(err));
        }
    });
}

export async function dbGet<T = unknown>(store: string, key: IDBValidKey): Promise<T | undefined> {
    const db = await openDatabase();
    return new Promise<T>((resolve, reject): void => {
        try {
            const transaction = db.transaction([store], "readonly");
            transaction.onabort = () => reject(toStorageError(transaction.error));
            transaction.onerror = () => reject(toStorageError(transaction.error));
            const request = transaction.objectStore(store).get(key);
            request.onsuccess = () => {
                try {
                    transaction.commit?.();
                    const result = request.result;
                    resolve(result);
                } catch (err) {
                    reject(toStorageError(err));
                }
            }
        } catch (err) {
            reject(toStorageError(err));
        }
    });
}

export async function dbCursor<T = unknown>(store: string, key: undefined | IDBValidKey | IDBKeyRange,
    fn: (value: T) => void, idx?: string
): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject): void => {
        try {
            const transaction = db.transaction([store], "readonly");
            transaction.onabort = () => reject(toStorageError(transaction.error));
            transaction.onerror = () => reject(toStorageError(transaction.error));
            let obj: IDBObjectStore | IDBIndex = transaction.objectStore(store);
            if (idx) {
                obj = obj.index(idx);
            }
            if (obj?.getAll) {
                const request = obj.getAll(key);
                request.onsuccess = () => {
                    const result = request.result;
                    try {
                        for (const item of result) {
                            fn(item);
                        }
                        transaction.commit?.();
                        resolve();
                    } catch (err) {
                        reject(toStorageError(err));
                    }
                }
            } else {
                const request = obj.openCursor(key);
                request.onsuccess = () => {
                    const cursor = request.result;
                    try {
                        if (cursor) {
                            fn(cursor.value);
                            cursor['continue']();
                        } else {
                            transaction.commit?.();
                            resolve();
                        }
                    } catch (err) {
                        reject(toStorageError(err));
                    }
                };
            }
        } catch (err) {
            reject(toStorageError(err));
        }
    });
}


export async function dbPut<T>(store: string, value: T, key?: IDBValidKey): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>(async (resolve, reject): Promise<void> => {
        try {
            const transaction = db.transaction([store], "readwrite");
            transaction.onabort = () => reject(toStorageError(transaction.error));
            transaction.onerror = () => reject(toStorageError(transaction.error));
            transaction.oncomplete = () => {
                try {
                    resolve();
                } catch (err) {
                    reject(toStorageError(err));
                }
            };
            transaction.objectStore(store).put(value, key).onsuccess = () => transaction.commit?.();
        } catch (err) {
            reject(toStorageError(err));
        }
    });
}

export async function dbUpdate<T>(store: string, key: IDBValidKey, fn: (value: T | undefined) => {key?: IDBValidKey, value: T} | undefined): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
        try {
            const transaction = db.transaction([store], "readwrite");
            transaction.onabort = () => reject(toStorageError(transaction.error));
            transaction.onerror = () => reject(toStorageError(transaction.error));
            transaction.oncomplete = () => {
                try {
                    resolve();
                } catch (err) {
                    reject(toStorageError(err));
                }
            };
            const objectStore = transaction.objectStore(store);
            const getRequest = objectStore.get(key);
            getRequest.onsuccess = () => {
                try {
                    const update = fn(getRequest.result);
                    if (update) {
                        const putRequest = objectStore.put(update.value, update.key);
                        putRequest.onsuccess = () => transaction.commit?.();
                    } else {
                        transaction.commit?.();
                    }
                } catch (err) {
                    reject(toStorageError(err));
                }
            }
        } catch (err) {
            reject(toStorageError(err));
        }
    });
}

export async function dbPutList<T>(store: string, list: {key: IDBValidKey, value: T}[]): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
        try {
            const transaction = db.transaction([store], "readwrite");
            transaction.onabort = () => reject(toStorageError(transaction.error));
            transaction.onerror = () => reject(toStorageError(transaction.error));
            transaction.oncomplete = () => {
                try {
                    resolve();
                } catch (err) {
                    reject(toStorageError(err));
                }
            };
            let count = 0;
            for (const item of list) {
                transaction.objectStore(store).put(item.value, item.key).onsuccess = () => {
                    if (++count === list.length) {
                        transaction.commit?.();
                    }
                };
            }
        } catch (err) {
            reject(toStorageError(err));
        }
    });
}

export async function dbPutKeyPath<T>(store: string, list: T[]): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject): void => {
        try {
            const transaction = db.transaction([store], "readwrite");
            transaction.onabort = () => reject(toStorageError(transaction.error));
            transaction.onerror = () => reject(toStorageError(transaction.error));
            transaction.oncomplete = () => {
                try {
                    resolve();
                } catch (err) {
                    reject(toStorageError(err));
                }
            };
            let count = 0;
            for (const item of list) {
                transaction.objectStore(store).put(item).onsuccess = () => {
                    if (++count === list.length) {
                        transaction.commit?.();
                    }
                }
            }
        } catch (err) {
            reject(toStorageError(err));
        }
    });
}

export async function dbDelete(store: string, key: IDBValidKey | IDBKeyRange): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject): void => {
        try {
            const transaction = db.transaction([store], "readwrite");
            transaction.objectStore(store).delete(key).onsuccess = () => transaction.commit?.();
            transaction.onabort = () => reject(toStorageError(transaction.error));
            transaction.onerror = () => reject(toStorageError(transaction.error));
            transaction.oncomplete = () => {
                try {
                    resolve();
                } catch (err) {
                    reject(toStorageError(err));
                }
            };
        } catch (err) {
            reject(toStorageError(err));
        }
    });
}

export async function dbClear(store: string): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject): void => {
        try {
            const transaction = db.transaction([store], "readwrite");
            transaction.objectStore(store).clear().onsuccess = () => transaction.commit?.();
            transaction.onabort = () => reject(toStorageError(transaction.error));
            transaction.onerror = () => reject(toStorageError(transaction.error));
            transaction.oncomplete = () => {
                try {
                    resolve();
                } catch (err) {
                    reject(toStorageError(err));
                }
            };
        } catch (err) {
            reject(toStorageError(err));
        }
    });
}

const stats = {
    clearSessionMinTime: Number.POSITIVE_INFINITY,
    clearSessionMaxTime: 0,
    clearSessionTotalTime: 0,
    clearSessionCount: 0,
    get clearSessionAvgTime() {
        return (this.clearSessionCount === 0) ? 0 : (this.clearSessionTotalTime / this.clearSessionCount);
    },
    deleteAllMinTime: Number.POSITIVE_INFINITY,
    deleteAllMaxTime: 0,
    deleteAllTotalTime: 0,
    deleteAllCount: 0,
    get deleteAllAvgTime() {
        return (this.deleteAllCount === 0) ? 0 : (this.deleteAllTotalTime / this.deleteAllCount);
    }
}

function logClearSessionTimeStats(time?: number): void {
    if (time !== undefined) {
        stats.clearSessionMinTime = Math.min(stats.clearSessionMinTime, time);
        stats.clearSessionMaxTime = Math.max(stats.clearSessionMaxTime, time);
        stats.clearSessionTotalTime += time;
        stats.clearSessionCount++;
    }
    console.info(`CLEAR_SESSION_TIME ${(time!==undefined)?`${time.toFixed(0)}ms `:''}min: ${stats.clearSessionMinTime.toFixed(0)}ms max: ${stats.clearSessionMaxTime.toFixed(0)}ms avg: ${stats.clearSessionAvgTime.toFixed(0)}ms`);
}

function logDeleteAllTimeStats(time?: number): void {
    if (time !== undefined) {
        stats.deleteAllMinTime = Math.min(stats.deleteAllMinTime, time);
        stats.deleteAllMaxTime = Math.max(stats.deleteAllMaxTime, time);
        stats.deleteAllTotalTime += time;
        stats.deleteAllCount++;
    }
    console.info(`DELETE_ALL_TIME ${(time!==undefined)?`${time.toFixed(0)}ms `:''}min: ${stats.deleteAllMinTime.toFixed(0)}ms max: ${stats.deleteAllMaxTime.toFixed(0)}ms avg: ${stats.deleteAllAvgTime.toFixed(0)}ms`);
}

export async function dbClearLogin(): Promise<void> {
    const t0 = performance.now();
    await dbClear('exams');
    await dbClear('encrypted');
    await dbClear('questions');
    await dbClear('images');
    await dbClear('answers');
    await dbClear('flags');
    await dbClear('users');
    await dbClear('notes');
    const t1 = performance.now();
    logDeleteAllTimeStats(t1 - t0);
}

export async function dbClearSelect(): Promise<void> {
    const t0 = performance.now();
    await dbClear('questions');
    await dbClear('images');
    await dbClear('answers');
    await dbClear('flags');
    await dbClear('notes');
    await dbDelete('users', 'language');
    await dbDelete('users', 'question');
    await dbDelete('users', 'state');
    await dbDelete('users', 'schedule');
    await dbDelete('users', 'scheduleVersion');
    await dbDelete('users', 'manifest');
    await dbDelete('users', 'exam');
    const t1 = performance.now();
    logClearSessionTimeStats(t1 - t0);
}
